"use client";

import { useState, useMemo } from "react";
import { Search, Plus, Minus, X } from "lucide-react";
import type { BlogPost, BlogCategory } from "@/payload-types";
import { ArticlesGridCard, ArticlesGridCardLink } from "./ArticlesGridCard";
import { GridGroupShell } from "@/payload/blocks/grid-sections/GridGroupShell";
import { GridSectionCrosshairs } from "@/payload/blocks/grid-sections/GridSectionCrosshairs";
import { cn } from "@/lib/utils";

const LINE_CSS_COLOR = "color-mix(in oklab, white 15%, transparent)";

interface ArticlesGridClientProps {
  heading?: string | null;
  description?: string | null;
  posts: BlogPost[];
  categories: BlogCategory[];
  lineColor: string;
  glowColor: string;
  postsPerPage: number;
  loadIncrement: number;
}

export function ArticlesGridClient({
  heading,
  description,
  posts,
  categories,
  lineColor,
  glowColor,
  postsPerPage,
  loadIncrement,
}: ArticlesGridClientProps) {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(postsPerPage);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const filteredPosts = useMemo(() => {
    let result = posts;

    if (activeCategory) {
      result = result.filter((post) =>
        post.categories?.some((cat) => {
          const catObj = typeof cat === "object" ? cat : null;
          if (!catObj) return false;
          if (catObj.slug === activeCategory) return true;
          const parentObj =
            typeof catObj.parent === "object" && catObj.parent !== null
              ? catObj.parent
              : null;
          return parentObj?.slug === activeCategory;
        })
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (post) =>
          post.title.toLowerCase().includes(q) ||
          post.excerpt?.toLowerCase().includes(q)
      );
    }

    return result;
  }, [posts, activeCategory, searchQuery]);

  const cellClass = "col-span-1 min-h-48 aspect-square 2xl:aspect-[4/3]";

  return (
    <>
      {/* Header section — 3-col grid with filters in left cell */}
      <GridGroupShell glowColor={glowColor}>
        <section
          className={cn(
            "relative w-full bg-black text-white grid-decorative-verticals",
            `border-x ${lineColor}`,
            `border-t ${lineColor}`,
            `border-b ${lineColor}`
          )}
          style={{ ["--grid-line-color" as string]: LINE_CSS_COLOR }}
        >
          {/* Mobile — single consolidated cell: heading + description centered, search bottom-right */}
          <div className="relative z-10 lg:hidden">
            <div className="decorative-grid-cell grid-cell col-span-1 aspect-square min-h-48">
              <div className="relative h-full p-3 sm:p-4">
                {/* Centered heading + description */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  {heading && (
                    <h2 className="font-subheading text-3xl text-white mb-3">
                      {heading}
                    </h2>
                  )}
                  {description && (
                    <p className="text-sm text-white/70 max-w-md">
                      {description}
                    </p>
                  )}
                </div>

                {/* Bottom row — search trigger collapsed, full-width input expanded */}
                <div className="absolute inset-x-3 bottom-3 sm:inset-x-4 sm:bottom-4 flex justify-end">
                  {!isMobileSearchOpen ? (
                    <button
                      type="button"
                      onClick={() => setIsMobileSearchOpen(true)}
                      className="relative inline-flex items-center gap-3 px-4 py-2.5 text-sm text-white/90 hover:text-white transition-colors"
                      aria-label="Open search"
                    >
                      {/* L-shaped corner brackets, right side */}
                      <span aria-hidden className="pointer-events-none absolute top-0 right-0 h-2.5 w-2.5 border-t border-r border-white/40" />
                      <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 h-2.5 w-2.5 border-b border-r border-white/40" />
                      <span className="font-subheading">Search</span>
                      <Search className="h-4 w-4" />
                    </button>
                  ) : (
                    <div className="relative w-full">
                      <input
                        type="text"
                        autoFocus
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-transparent border border-white/30 text-sm text-white placeholder:text-white/50 pl-3 pr-10 py-2.5 focus:outline-none focus:border-white/60 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setIsMobileSearchOpen(false);
                          setSearchQuery("");
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                        aria-label="Close search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop — original 3-col layout */}
          <div className="relative z-10 hidden lg:grid lg:grid-cols-3">
            {/* Left — category filters, bottom-left aligned */}
            <div className={cn("decorative-grid-cell grid-cell lg:border-t-0", cellClass)}>
              <div className="flex flex-col justify-end h-full p-3 sm:p-4 lg:p-5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  {categories.map((cat) => {
                    const isActive = activeCategory === cat.slug;
                    return (
                      <button
                        key={cat.id}
                        onClick={() =>
                          setActiveCategory((prev) =>
                            prev === cat.slug ? null : cat.slug
                          )
                        }
                        className={cn(
                          "group inline-flex items-center gap-2 text-sm transition-colors",
                          isActive ? "text-white" : "text-white/70 hover:text-white"
                        )}
                      >
                        <span
                          className={cn(
                            "font-subheading text-lg leading-none transition-colors",
                            isActive ? "text-white" : "text-white/40 group-hover:text-white/70"
                          )}
                        >
                          [
                        </span>
                        <span className="font-subheading">{cat.name}</span>
                        <Plus className="h-3.5 w-3.5" />
                        <span
                          className={cn(
                            "font-subheading text-lg leading-none transition-colors",
                            isActive ? "text-white" : "text-white/40 group-hover:text-white/70"
                          )}
                        >
                          ]
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Center — heading + description */}
            <div className={cn("decorative-grid-cell grid-cell lg:border-t-0", cellClass)}>
              <div className="flex flex-col items-center justify-center h-full p-3 sm:p-4 lg:p-5 text-center">
                {heading && (
                  <h2 className="font-subheading text-3xl lg:text-4xl 2xl:text-5xl text-white mb-3">
                    {heading}
                  </h2>
                )}
                {description && (
                  <p className="text-base lg:text-base 2xl:text-lg text-white/70 max-w-md">
                    {description}
                  </p>
                )}
              </div>
            </div>

            {/* Right — search, bottom-right aligned (mirrors filters on left) */}
            <div className={cn("decorative-grid-cell grid-cell lg:border-t-0", cellClass)}>
              <div className="flex flex-col justify-end items-end h-full p-3 sm:p-4 lg:p-5">
                <div className="relative w-full max-w-[280px]">
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-transparent border border-white/30 text-sm text-white placeholder:text-white/50 pl-3 pr-9 py-2 focus:outline-none focus:border-white/60 transition-colors"
                  />
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
                </div>
              </div>
            </div>
          </div>
          <GridSectionCrosshairs />
        </section>
      </GridGroupShell>

      {/* Post rows — pairs of articles, alternating 1col+2col / 2col+1col */}
      {(() => {
        const visiblePosts = filteredPosts.slice(0, visibleCount);
        const pairs: BlogPost[][] = [];
        for (let i = 0; i < visiblePosts.length; i += 2) {
          pairs.push(visiblePosts.slice(i, i + 2));
        }

        if (pairs.length === 0) {
          return (
            <div className={cn("bg-black text-center py-16", `border-x ${lineColor}`, `border-b ${lineColor}`)}>
              <p className="text-white/50 text-sm">No articles found.</p>
            </div>
          );
        }

        const hasMore = visibleCount < filteredPosts.length;
        const canCollapse = visibleCount > postsPerPage && filteredPosts.length > postsPerPage;

        return (
          <>
            {/* Mobile — peek carousel: full-bleed horizontal scroll, top/bottom borders only */}
            <section
              className={cn(
                "relative w-full bg-black text-white lg:hidden",
                `border-b ${lineColor}`
              )}
            >
              <ul
                className={cn(
                  "flex overflow-x-auto snap-x snap-mandatory gap-5 py-5 pl-5 pr-8",
                  "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                )}
              >
                {filteredPosts.map((post) => (
                  <li
                    key={post.id}
                    className="snap-start shrink-0 basis-[90%] aspect-[4/5]"
                  >
                    <ArticlesGridCardLink post={post} />
                  </li>
                ))}
              </ul>
            </section>

            {/* Desktop — paired rows with grid lines and Show More */}
            <div className="hidden lg:block">
              <GridGroupShell glowColor={glowColor}>
                {pairs.map((pair, pairIndex) => {
                  // First pair: article1 (2col) + article2 (1col) — latest leads wide
                  // Then alternates: 1col + 2col, 2col + 1col, ...
                  const leadWide = pairIndex % 2 === 0;

                  return (
                    <section
                      key={pair[0].id}
                      className={cn(
                        "relative w-full bg-black text-white grid-decorative-verticals",
                        `border-x ${lineColor}`,
                        `border-b ${lineColor}`
                      )}
                      style={{ ["--grid-line-color" as string]: LINE_CSS_COLOR }}
                    >
                      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 px-4 sm:px-6 lg:px-0">
                        {leadWide ? (
                          <>
                            <ArticlesGridCard post={pair[0]} span={2} />
                            {pair[1] && <ArticlesGridCard post={pair[1]} span={1} />}
                          </>
                        ) : (
                          <>
                            <ArticlesGridCard post={pair[0]} span={1} />
                            {pair[1] && <ArticlesGridCard post={pair[1]} span={2} />}
                          </>
                        )}
                      </div>
                      <GridSectionCrosshairs />
                    </section>
                  );
                })}

                {(hasMore || canCollapse) && (
                  <section
                    className={cn(
                      "relative w-full bg-black text-white grid-decorative-verticals",
                      `border-x ${lineColor}`,
                      `border-b ${lineColor}`
                    )}
                    style={{ ["--grid-line-color" as string]: LINE_CSS_COLOR }}
                  >
                    <div className="relative z-10 flex flex-wrap items-center justify-center gap-3 py-10">
                      {hasMore && (
                        <button
                          type="button"
                          onClick={() =>
                            setVisibleCount((prev) =>
                              Math.min(prev + loadIncrement, filteredPosts.length)
                            )
                          }
                          className="inline-flex items-center gap-2 text-sm text-white border border-white/30 px-5 py-2.5 hover:border-white/60 transition-colors"
                        >
                          Show More
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {canCollapse && (
                        <button
                          type="button"
                          onClick={() => setVisibleCount(postsPerPage)}
                          className="inline-flex items-center gap-2 text-sm text-white border border-white/30 px-5 py-2.5 hover:border-white/60 transition-colors"
                        >
                          Show Less
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <GridSectionCrosshairs />
                  </section>
                )}
              </GridGroupShell>
            </div>
          </>
        );
      })()}
    </>
  );
}
